import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  PlusIcon,
  DocumentTextIcon,
  CalendarIcon,
  UserIcon,
  TrashIcon,
  BookmarkIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { BookmarkIcon as BookmarkSolidIcon } from "@heroicons/react/24/solid";
import {
  researchService,
  ResearchNote,
  ResearchNoteType,
  ResearchEntityType,
  ResearchNoteCreate,
  ResearchSourceType,
  ResearchVisibility,
} from "../services/researchService";
import toast from "react-hot-toast";
import MarkdownEditor from "./markdown/MarkdownEditor";
import MarkdownViewer from "./markdown/MarkdownViewer";
import type { MultiEntityMentionsEditorRef } from "./MultiEntityMentionsEditor";

interface ResearchTabProps {
  organizationId: string;
  entityType: ResearchEntityType;
  entityId: string;
  entityName?: string;
  canEdit: boolean;
}

const ResearchTab: React.FC<ResearchTabProps> = ({
  organizationId,
  entityType,
  entityId,
  entityName,
  canEdit,
}) => {
  const [researchNotes, setResearchNotes] = useState<ResearchNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState<ResearchNote | null>(null);
  const [formData, setFormData] = useState<Partial<ResearchNoteCreate>>({
    entity_type: entityType,
    entity_id: entityId,
    title: "",
    content: "",
    note_type: ResearchNoteType.GENERAL,
    source_type: ResearchSourceType.MANUAL,
    visibility: ResearchVisibility.PUBLIC,
    is_pinned: false,
  });
  const contentEditorRef = useRef<MultiEntityMentionsEditorRef>(null);

  // Fetch research notes for this entity
  const fetchResearchNotes = useCallback(async () => {
    if (!organizationId || !entityId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const notes = await researchService.getEntityResearch(
        organizationId,
        entityType,
        entityId,
        false,
      );
      setResearchNotes(notes);
    } catch (error) {
      console.error("Error fetching research notes:", error);
      toast.error("Failed to load research notes");
    } finally {
      setLoading(false);
    }
  }, [organizationId, entityType, entityId]);

  useEffect(() => {
    fetchResearchNotes();
  }, [fetchResearchNotes]);

  // Create new research note
  const handleCreate = async () => {
    const tokenizedContent =
      contentEditorRef.current?.getTokenizedContent() || formData.content;

    if (!formData.title || !tokenizedContent) {
      toast.error("Title and content are required");
      return;
    }

    try {
      await researchService.createResearchNote(organizationId, {
        ...formData,
        content: tokenizedContent,
        entity_type: entityType,
        entity_id: entityId,
        // Ensure we send the enum values (strings), not enum objects
        note_type: formData.note_type || ResearchNoteType.GENERAL,
        source_type: formData.source_type || ResearchSourceType.MANUAL,
        visibility: formData.visibility || ResearchVisibility.PUBLIC,
      } as ResearchNoteCreate);

      toast.success("Research note created");
      setShowCreateModal(false);
      setFormData({
        entity_type: entityType,
        entity_id: entityId,
        title: "",
        content: "",
        note_type: ResearchNoteType.GENERAL,
        source_type: ResearchSourceType.MANUAL,
        visibility: ResearchVisibility.PUBLIC,
        is_pinned: false,
      });
      // Refresh the list to show the new note
      fetchResearchNotes();
    } catch (error) {
      toast.error("Failed to create research note");
    }
  };

  // Toggle pin status
  const togglePin = async (note: ResearchNote) => {
    if (!canEdit) return;

    try {
      await researchService.updateResearchNote(organizationId, note.id, {
        is_pinned: !note.is_pinned,
      });
      toast.success(note.is_pinned ? "Unpinned" : "Pinned");
      fetchResearchNotes();
    } catch (error) {
      toast.error("Failed to update pin status");
    }
  };

  // Delete research note
  const deleteNote = async (noteId: string) => {
    if (!canEdit) return;

    if (
      !window.confirm("Are you sure you want to delete this research note?")
    ) {
      return;
    }

    try {
      await researchService.deleteResearchNote(organizationId, noteId);
      toast.success("Research note deleted");
      fetchResearchNotes();
      setSelectedNote(null);
    } catch (error) {
      toast.error("Failed to delete research note");
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get note type icon
  const getNoteTypeIcon = (type: ResearchNoteType) => {
    switch (type) {
      case ResearchNoteType.INSIGHT:
        return "💡";
      case ResearchNoteType.RESEARCH:
        return "🔬";
      case ResearchNoteType.MEETING:
        return "🤝";
      case ResearchNoteType.ANALYSIS:
        return "📊";
      case ResearchNoteType.RECOMMENDATION:
        return "📝";
      default:
        return "📄";
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Research & Intelligence</h2>
        {canEdit && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Research
          </button>
        )}
      </div>

      {/* Research Notes List */}
      {researchNotes.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            No research notes yet
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by adding research about this {entityType.toLowerCase()}
            .
          </p>
          {canEdit && (
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add First Research Note
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {/* Pinned Notes */}
          {researchNotes.filter((note) => note.is_pinned).length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                <BookmarkSolidIcon className="w-4 h-4 text-yellow-500 mr-2" />
                Pinned Research
              </h3>
              <div className="space-y-3">
                {researchNotes
                  .filter((note) => note.is_pinned)
                  .map((note) => (
                    <ResearchNoteCard
                      key={note.id}
                      note={note}
                      canEdit={canEdit}
                      onTogglePin={togglePin}
                      onDelete={deleteNote}
                      onClick={() => setSelectedNote(note)}
                    />
                  ))}
              </div>
            </div>
          )}

          {/* Regular Notes */}
          <div className="space-y-3">
            {researchNotes
              .filter((note) => !note.is_pinned)
              .map((note) => (
                <ResearchNoteCard
                  key={note.id}
                  note={note}
                  canEdit={canEdit}
                  onTogglePin={togglePin}
                  onDelete={deleteNote}
                  onClick={() => setSelectedNote(note)}
                />
              ))}
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-500/75"
              onClick={() => setShowCreateModal(false)}
            />
            <div className="relative bg-white rounded-lg max-w-2xl w-full p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold">Add Research Note</h3>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData({ ...formData, title: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter research title..."
                  />
                </div>

                {/* Note Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    value={formData.note_type}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        note_type: e.target.value as ResearchNoteType,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.values(ResearchNoteType).map((type) => (
                      <option key={type} value={type}>
                        {getNoteTypeIcon(type)}{" "}
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Content *
                  </label>
                  <MarkdownEditor
                    ref={contentEditorRef}
                    value={formData.content || ""}
                    onChange={(value) =>
                      setFormData({ ...formData, content: value })
                    }
                    placeholder="Enter research content... Type @ to mention OKRs, Segments, Propositions, or Companies"
                    organizationId={organizationId}
                  />
                </div>

                {/* Pin */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="pin-note"
                    checked={formData.is_pinned}
                    onChange={(e) =>
                      setFormData({ ...formData, is_pinned: e.target.checked })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="pin-note"
                    className="ml-2 text-sm text-gray-700"
                  >
                    Pin this note to the top
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Research Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {selectedNote && (
        <ResearchNoteModal
          note={selectedNote}
          canEdit={canEdit}
          onClose={() => setSelectedNote(null)}
          onDelete={deleteNote}
        />
      )}
    </div>
  );
};

// Research Note Card Component
const ResearchNoteCard: React.FC<{
  note: ResearchNote;
  canEdit: boolean;
  onTogglePin: (note: ResearchNote) => void;
  onDelete: (id: string) => void;
  onClick: () => void;
}> = ({ note, canEdit, onTogglePin, onDelete, onClick }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getNoteTypeIcon = (type: ResearchNoteType) => {
    switch (type) {
      case ResearchNoteType.INSIGHT:
        return "💡";
      case ResearchNoteType.RESEARCH:
        return "🔬";
      case ResearchNoteType.MEETING:
        return "🤝";
      case ResearchNoteType.ANALYSIS:
        return "📊";
      case ResearchNoteType.RECOMMENDATION:
        return "📝";
      default:
        return "📄";
    }
  };

  return (
    <div
      className="bg-white rounded-lg border hover:shadow-md transition-shadow cursor-pointer p-4"
      onClick={onClick}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center mb-2">
            <span className="text-lg mr-2">
              {getNoteTypeIcon(note.note_type)}
            </span>
            <h3 className="text-lg font-semibold text-gray-900">
              {note.title}
            </h3>
          </div>
          <div className="text-sm text-gray-600 line-clamp-2 mb-3">
            <MarkdownViewer content={note.content} />
          </div>
          <div className="flex items-center space-x-4 text-xs text-gray-500">
            <div className="flex items-center">
              <UserIcon className="w-4 h-4 mr-1" />
              {note.created_by_name || "Unknown"}
            </div>
            <div className="flex items-center">
              <CalendarIcon className="w-4 h-4 mr-1" />
              {formatDate(note.created_at)}
            </div>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTogglePin(note);
              }}
              className="text-gray-400 hover:text-yellow-500"
            >
              {note.is_pinned ? (
                <BookmarkSolidIcon className="w-5 h-5 text-yellow-500" />
              ) : (
                <BookmarkIcon className="w-5 h-5" />
              )}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(note.id);
              }}
              className="text-gray-400 hover:text-red-600"
            >
              <TrashIcon className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Research Note Modal Component
const ResearchNoteModal: React.FC<{
  note: ResearchNote;
  canEdit: boolean;
  onClose: () => void;
  onDelete: (id: string) => void;
}> = ({ note, canEdit, onClose, onDelete }) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getNoteTypeIcon = (type: ResearchNoteType) => {
    switch (type) {
      case ResearchNoteType.INSIGHT:
        return "💡";
      case ResearchNoteType.RESEARCH:
        return "🔬";
      case ResearchNoteType.MEETING:
        return "🤝";
      case ResearchNoteType.ANALYSIS:
        return "📊";
      case ResearchNoteType.RECOMMENDATION:
        return "📝";
      default:
        return "📄";
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div
          className="fixed inset-0 bg-gray-500/75"
          onClick={onClose}
        />
        <div className="relative bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-6 py-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                  <span className="mr-2">
                    {getNoteTypeIcon(note.note_type)}
                  </span>
                  {note.title}
                </h2>
                <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                  <span>Type: {note.note_type}</span>
                  <span>Source: {note.source_type.replace(/_/g, " ")}</span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="px-6 py-4">
            {/* Metadata */}
            <div className="mb-6 text-sm text-gray-600">
              <div className="mb-2">
                Created by {note.created_by_name || "Unknown"} on{" "}
                {formatDate(note.created_at)}
              </div>
              {note.updated_by && (
                <div>
                  Last updated by {note.updated_by_name || "Unknown"} on{" "}
                  {formatDate(note.updated_at)}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="prose max-w-none">
              <div className="text-gray-700">
                <MarkdownViewer content={note.content} />
              </div>
            </div>

            {/* Attachments */}
            {note.attachments && note.attachments.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Attachments ({note.attachments.length})
                </h3>
                <div className="space-y-2">
                  {note.attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center p-3 bg-gray-50 rounded-lg"
                    >
                      <DocumentTextIcon className="w-5 h-5 text-gray-400 mr-3" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">
                          {attachment.attachment_type}
                        </p>
                        {attachment.attachment_type === "url" &&
                          attachment.attachment_data.url && (
                            <a
                              href={attachment.attachment_data.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-700"
                            >
                              {attachment.attachment_data.url}
                            </a>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            {canEdit && (
              <div className="mt-6 pt-4 border-t flex justify-end">
                <button
                  onClick={() => {
                    onDelete(note.id);
                    onClose();
                  }}
                  className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                >
                  <TrashIcon className="w-4 h-4 mr-2" />
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResearchTab;
